import React from "react";

import { useSuspenseQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom-v5-compat";
import * as z from "zod";

import { Button, PageContent, PrimaryAction } from "../../Atoms";
import { useLoadingState } from "../../Loading";
import { useVenue } from "../../VenueContext";
import { CartQueries } from "../Cart/queries";
import { updateCart } from "../Cart/requests";
import { useSession, useSessionDispatch } from "../Session/SessionContext";
import { CheckoutSessionQueries } from "./queries";
import { CheckoutSession, fetchCheckoutSession } from "./request";

export const TabCheckoutSuccess = () => {
  const { cart } = useSession();
  const dispatch = useSessionDispatch();
  const navigate = useNavigate();
  const { checkoutId } = useParams();
  const { t } = useTranslation("onsite");
  const { name: venueName } = useVenue();
  const [checkoutSession, setCheckoutSession] =
    React.useState<CheckoutSession | null>();

  const loadingState = useLoadingState(async () => {
    if (!checkoutId) return;
    const checkoutSession = await fetchCheckoutSession(checkoutId);
    console.log({ checkoutSession });
    setCheckoutSession(checkoutSession);
  }, []);

  const startNewTab = async () => {
    const response = await updateCart({
      uuid: cart.uuid,
      customerTabId: null,
    });
    dispatch({
      action: "updateCart",
      payload: response,
    });
    dispatch({ action: "updateCustomerTab", payload: null });
    navigate("/");
  };
  if (!checkoutSession || loadingState !== "LOADED") {
    return <div> Loading Checkout session</div>;
  }

  const primaryAction = (
    <Button onClick={startNewTab} primary>
      {t("orderConfirmed.startNewOrder")}
    </Button>
  );

  return (
    <PageContent className="space-y-32">
      <div className="mx-auto max-w-md md:space-y-64">
        <div>
          <div className="flex-1 space-y-16 text-center">
            <div className="text-3xl font-semibold">
              {t("paymentConfirmed.paymentConfirmed", { venueName })}
            </div>
            <div>{t("paymentConfirmed.farewellMessage", { venueName })}</div>
          </div>
        </div>

        <div className="flex">
          <div className="flex h-full min-h-256 flex-1 flex-col justify-between">
            <div className="hidden md:block"> {primaryAction}</div>
            <PrimaryAction>{primaryAction}</PrimaryAction>
          </div>
        </div>
      </div>
    </PageContent>
  );
};
const QueryParamsSchema = z.object({
  checkoutId: z.string(),
});
const CheckoutSuccess = () => {
  const { cart } = useSession();
  const dispatch = useSessionDispatch();
  const navigate = useNavigate();
  const { checkoutId } = QueryParamsSchema.parse(useParams());
  const { t } = useTranslation("onsite");
  const { name: venueName } = useVenue();

  const checkoutSession = useSuspenseQuery(
    CheckoutSessionQueries.byUuid(checkoutId),
  ).data;

  const completedCart = useSuspenseQuery({
    ...CartQueries.cart(checkoutSession.originatorId),
  }).data;

  const startNewTab = async () => {
    const response = await updateCart({
      uuid: cart.uuid,
      customerTabId: null,
    });
    dispatch({
      action: "updateCart",
      payload: response,
    });
    dispatch({ action: "updateCustomerTab", payload: null });
    navigate("/");
  };

  const primaryAction = (
    <Button onClick={startNewTab} primary>
      {t("orderConfirmed.startNewOrder")}
    </Button>
  );

  return (
    <PageContent className="space-y-32">
      <div className="mx-auto max-w-md md:space-y-64">
        {checkoutSession.originatorType == "CustomerTab" && (
          <div>
            <div className="flex-1 space-y-16 text-center">
              <div className="text-lg font-semibold">
                {t("paymentConfirmed.paymentConfirmed")}
              </div>
              <div className="text-center text-3xl font-bold"></div>
            </div>
          </div>
        )}
        <div>
          <div className="flex-1 space-y-32 text-center">
            <div>
              <div className="text-lg font-semibold">
                {t("orderConfirmed.thanks")}
              </div>
              <div className="text-3xl font-bold">{completedCart?.name}</div>
            </div>
            <div>
              <div className="text-lg font-semibold">
                {t("orderConfirmed.yourOrderNumberIs")}
              </div>
              <div className="text-center text-3xl font-bold">
                {completedCart?.orderNumber}{" "}
              </div>
            </div>
            {completedCart?.pickUpAt ? (
              <div>
                <div className="text-lg font-semibold">
                  {t("orderConfirmed.orderRequestedFor", {
                    venueName,
                  })}
                </div>
                <div className="text-3xl font-bold">
                  {DateTime.fromISO(completedCart.pickUpAt).toLocaleString(
                    DateTime.TIME_SIMPLE,
                  )}
                </div>
              </div>
            ) : (
              <div className="text-lg font-semibold">
                {t("orderConfirmed.orderWillBeReadySoon", { venueName })}
              </div>
            )}
            <div className="text-lg font-semibold">
              {t("orderConfirmed.seeYouSoon")}
            </div>
          </div>
        </div>
        <div className="flex">
          <div className="flex h-full min-h-256 flex-1 flex-col justify-between">
            <div className="hidden md:block"> {primaryAction}</div>
            <PrimaryAction>{primaryAction}</PrimaryAction>
          </div>
        </div>
      </div>
    </PageContent>
  );
};

export default CheckoutSuccess;
